import { useEffect, useRef } from 'react';
import { usePageDataContext } from '$/(cms)/_modules/cmsService/pageDataService/pageDataProvider';
import { feApiFrontClient } from '$/(cms)/_modules/feApiClient/feApiFrontClient';

const POLLING_INTERVAL_MS = 1_500;

const delay = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Client-side continuation of a module's quote poll (CMS-1896). Module-generic:
 * works for any module instance with a slice in `moduleTravelQuotes` — today
 * that is priced Table Builders, tomorrow any component quoting its own trip.
 *
 * The SSR pass takes the quote service's FIRST snapshot only, so bots and the
 * first paint get whatever prices were ready; when that snapshot's status is
 * `inprogress`, this hook keeps polling the same criteria and publishes each
 * tick into the module's slice via `setModuleTravelQuotes` — the unquoted price
 * cells show loaders until their quote arrives (or the poll settles and they
 * fall to "-").
 *
 * A FAILED SSR pass is retried once client-side, the same guard the page-wide
 * twin has (`useTravelSearchPoll`'s "SSR backend req was not successful"
 * effect): a transient InsureMO failure baked into an ISR snapshot must not
 * leave every visitor of that window with a dash-only column. The service
 * evicts a failed in-flight entry, so the retry is a real re-quote. Client
 * ticks publish `isBackendData: false`, so a retry that fails again never arms
 * another one.
 *
 * The criteria are fixed module fields, so unlike `useTravelSearchPoll` there
 * is no re-search: the poll runs at most once per mount.
 */
export function useModuleQuotesPoll(moduleId: string): void {
  const {
    pageData: { moduleTravelQuotes },
    setModuleTravelQuotes,
  } = usePageDataContext();

  const quotes = moduleTravelQuotes?.[moduleId];

  // The loop reads the slice through a ref rather than through the effect's
  // dependencies, because it publishes into that same slice: every tick goes
  // out through `setModuleTravelQuotes`, which stamps `isBackendData: false`.
  // Depending on a value the loop itself changes cancelled the loop after its
  // FIRST request — the dependency flipped, React ran the cleanup, and the
  // still-running loop returned out of its `delay`, leaving the status at
  // `inprogress` and every unquoted price cell on its loader for good.
  const live = useRef({ quotes, setModuleTravelQuotes });
  live.current = { quotes, setModuleTravelQuotes };

  useEffect(() => {
    // Armed once per mount, from the slice this mount started with: continue an
    // unfinished SSR poll, or retry a failed SSR pass — never a client-published
    // tick (those carry `isBackendData: false`).
    const ssrSlice = live.current.quotes;
    const params = ssrSlice?.params;
    if (!ssrSlice?.isBackendData || ssrSlice.status === 'success' || !params) {
      return;
    }

    let cancelled = false;

    const poll = async () => {
      let status = 'inprogress';
      while (status === 'inprogress' && !cancelled) {
        await delay(POLLING_INTERVAL_MS);
        if (cancelled) return;
        try {
          const next = await feApiFrontClient.travelCompareQuotes(params);
          if (cancelled) return;
          status = next.status;
          live.current.setModuleTravelQuotes(moduleId, {
            premiums: next.data,
            status: next.status,
            params,
          });
        } catch {
          // Same guard as the success path: a request that rejects after the
          // cleanup ran must not publish either — a client-published `fail`
          // carries `isBackendData: false`, which nothing re-arms from.
          if (cancelled) return;
          // Settle the slice so the loaders resolve to "-" rather than
          // spinning forever; the last published premiums stay in place.
          status = 'fail';
          live.current.setModuleTravelQuotes(moduleId, {
            premiums: live.current.quotes?.premiums,
            status: 'fail',
            params,
          });
        }
      }
    };

    void poll();
    // Cancels on unmount (and on a module change), which is the only reason to
    // stop: the loop's own exit condition is the settled status.
    return () => {
      cancelled = true;
    };
    // Everything the loop needs is read from `live`, so the arming decision
    // depends on the module alone.
  }, [moduleId]);
}
